import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { TranslateService } from '../../services/translate.service';
import { GuestReservation, GuestReservationResponse } from '../../models/Order';
import { ActionButtonComponent } from '../buttons/action-button.component';
import { BaseButtonComponent } from '../buttons/base-button.component';
import { LinkButtonComponent } from '../buttons/link-button.component';
import { LoadingSpinnerComponent } from '../ui/loading-spinner-component';
import { partySizes, reservationChange, ReservationDraft, timesToShow } from '../../utils/reservation-change';
import { OFFERS_ASK, OFFERS_RECEIVES } from '../../utils/marketing';

// Guest texts for samwise's /guest/reservation_* error codes
const RESERVATION_ERRORS: Record<string, string> = {
  'reservation_not_found': 'Vi hittar inte bokningen. Kontrollera länken.',
  'reservation_not_changeable': 'Bokningen kan inte längre ändras här. Kontakta restaurangen.',
  'time_not_available': 'Tiden är inte längre ledig. Välj en annan tid.',
  'no_tables_available': 'Det finns inget ledigt bord då. Välj en annan tid.',
  'party_size_too_small': 'Sällskapet är för litet för en bokning här. Kontakta restaurangen.',
  'party_size_too_large': 'Sällskapet är för stort för en bokning här. Kontakta restaurangen.',
  'invalid_party_size': 'Välj hur många ni är.',
  'date_in_past': 'Datumet har redan passerat.',
};

// Where the reminder's link lands: /:venue_id/reservation/:ref?t=token. The guest sees the booking, confirms they are
// coming, changes the date, time or party, or cancels it; and says yes to offers, or no thanks to them.
@Component({
  selector: 'app-reservation-manage',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent, BaseButtonComponent, LinkButtonComponent, LoadingSpinnerComponent],
  template: `
    <div class="max-w-lg mx-auto flex-col pt-20 text-theme-text">
      <div class="flex-1 bg-theme-surface-95 backdrop-blur-2xl rounded-t-3xl min-h-[calc(100vh-5rem)]">

        <div class="pt-6 px-8">
          <div class="font-bold text-xl">{{ getString('Din bokning') }}</div>
          @if (reservation()?.venue_name) {
            <div class="text-theme-text2 mt-1">{{ reservation()?.venue_name }}</div>
          }
        </div>

        <div class="px-8 pb-12">
          @if (isLoading()) {
            <div class="flex justify-center py-12">
              <app-loading-spinner size="lg"></app-loading-spinner>
            </div>
          } @else if (loadError()) {
            <div class="text-center pt-12">
              <i class="fas fa-circle-exclamation text-4xl text-theme-text3 mb-4"></i>
              <p class="text-theme-text2">{{ loadError() }}</p>
            </div>
          } @else {
          @if (reservation(); as r) {

            @if (notice()) {
              <div class="mt-6 p-4 bg-green-100 border border-green-300 rounded-xl text-green-800 text-sm flex items-center gap-2">
                <i class="fas fa-check"></i>
                <span>{{ notice() }}</span>
              </div>
            }

            @if (r.status === 'cancelled') {
              <div class="text-center pt-8">
                <i class="fas fa-calendar-xmark text-4xl text-theme-text3 mb-4"></i>
                <h2 class="text-xl font-bold">{{ getString('Bokningen är avbokad') }}</h2>
              </div>
            }

            @if (!editing()) {
              <div class="bg-theme-surface2 rounded-2xl p-5 mt-6 space-y-3">
                <div class="text-center pb-3 border-b border-theme-divider">
                  <div class="text-sm text-theme-text2 mb-1">{{ getString('Bokningsnummer') }}</div>
                  <div class="text-2xl font-bold font-mono tracking-wider">{{ r.booking_ref }}</div>
                </div>
                @if (r.guest_name) {
                  <div class="flex justify-between items-center">
                    <span class="flex items-center gap-2 text-theme-text2">
                      <i class="fas fa-id-card text-xs w-4"></i>
                      <span>{{ getString('Namn') }}</span>
                    </span>
                    <span class="font-medium">{{ r.guest_name }}</span>
                  </div>
                }
                @if (r.booking_type_name) {
                  <div class="flex justify-between items-center">
                    <span class="flex items-center gap-2 text-theme-text2">
                      <i class="fas fa-utensils text-xs w-4"></i>
                      <span>{{ getString('Typ') }}</span>
                    </span>
                    <span class="font-medium">{{ r.booking_type_name }}</span>
                  </div>
                }
                <div class="flex justify-between items-center">
                  <span class="flex items-center gap-2 text-theme-text2">
                    <i class="fas fa-user text-xs w-4"></i>
                    <span>{{ getString('Antal') }}</span>
                  </span>
                  <span class="font-medium">{{ r.party_size }}{{ childrenSuffix(r) }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="flex items-center gap-2 text-theme-text2">
                    <i class="fas fa-calendar text-xs w-4"></i>
                    <span>{{ getString('Datum') }}</span>
                  </span>
                  <span class="font-medium capitalize">{{ formatDate(r.date_key) }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="flex items-center gap-2 text-theme-text2">
                    <i class="fas fa-clock text-xs w-4"></i>
                    <span>{{ getString('Tid') }}</span>
                  </span>
                  <span class="font-medium">{{ r.time }}</span>
                </div>
                @if (r.confirmed && r.status !== 'cancelled') {
                  <div class="flex justify-center items-center gap-2 pt-3 border-t border-theme-divider text-green-700 font-medium">
                    <i class="fas fa-circle-check"></i>
                    <span>{{ getString('Bokningen är bekräftad') }}</span>
                  </div>
                }
              </div>

              @if (r.can_confirm) {
                <div class="mt-8">
                  @if (r.confirm_by) {
                    <p class="text-center text-theme-text2 mb-3">
                      {{ getString('Bekräfta din bokning senast kl %s, annars förfaller den.').replace('%s', r.confirm_by) }}
                    </p>
                  }
                  <app-action-button
                    icon="fa-check"
                    [text]="getString('Jag kommer')"
                    [chevron]="false"
                    [disabled]="isSaving()"
                    [spinner]="isSaving()"
                    (onClick)="confirm()">
                  </app-action-button>
                </div>
              }

              @if (r.can_change) {
                <div class="mt-8">
                  <app-action-button
                    icon="fa-pen"
                    [text]="getString('Ändra bokningen')"
                    [chevron]="false"
                    (onClick)="startEditing(r)">
                  </app-action-button>
                </div>
              } @else if (r.status !== 'cancelled' && !r.can_cancel) {
                <p class="text-theme-text2 text-center mt-6">{{ getString('Bokningen kan inte längre ändras här. Kontakta restaurangen.') }}</p>
              }
            } @else {
              <!-- Change: party, date, then a free time -->
              <div class="mt-6 space-y-6">
                <div>
                  <label class="block font-medium mb-2" for="party">{{ getString('Antal personer') }}</label>
                  <select id="party" class="w-full bg-theme-surface2 rounded-xl px-4 py-3 text-theme-text"
                          [value]="draft().partySize" (change)="setParty($event)">
                    @for (size of sizes(); track size) {
                      <option [value]="size" [selected]="size === draft().partySize">
                        {{ size }} {{ size === 1 ? getString('person') : getString('personer') }}
                      </option>
                    }
                  </select>
                </div>
                <div>
                  <label class="block font-medium mb-2" for="date">{{ getString('Datum') }}</label>
                  <select id="date" class="w-full bg-theme-surface2 rounded-xl px-4 py-3 text-theme-text capitalize"
                          [value]="draft().dateKey" (change)="setDate($event)">
                    @for (day of r.days ?? []; track day) {
                      <option [value]="day" [selected]="day === draft().dateKey">{{ formatDate(day) }}</option>
                    }
                  </select>
                </div>
                <div>
                  <div class="font-medium mb-2">{{ getString('Tid') }}</div>
                  @if (timesLoading()) {
                    <div class="flex justify-center py-6">
                      <i class="fas fa-spinner fa-spin text-2xl text-theme-text3"></i>
                    </div>
                  } @else if (times().length === 0) {
                    <p class="text-theme-text2">{{ getString('Inga tillgängliga tider för detta datum') }}</p>
                  } @else {
                    <div class="grid grid-cols-3 gap-2">
                      @for (time of times(); track time) {
                        <app-base-button baseClass="w-full" stateColorScheme="surface" (onClick)="setTime(time)">
                          <div class="py-3 px-2 rounded-xl text-center font-medium transition-all w-full"
                               [class.bg-theme-primary]="draft().time === time"
                               [class.text-theme-contrast]="draft().time === time"
                               [class.bg-theme-surface2]="draft().time !== time">
                            {{ time }}
                          </div>
                        </app-base-button>
                      }
                    </div>
                  }
                </div>
                <app-action-button
                  [text]="getString('Spara ändringen')"
                  [chevron]="false"
                  [spinner]="isSaving()"
                  [disabled]="!change() || isSaving()"
                  (onClick)="save()">
                </app-action-button>
                <div class="flex justify-center">
                  <app-link-button font="font-medium" [text]="getString('Avbryt')" (onClick)="stopEditing()"></app-link-button>
                </div>
              </div>
            }

            @if (actionError()) {
              <div class="mt-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-800 text-sm">
                <div class="flex items-center gap-2">
                  <i class="fas fa-exclamation-circle"></i>
                  <span>{{ actionError() }}</span>
                </div>
              </div>
            }

            @if (r.marketing && !editing()) {
              <!-- Offers by e-mail and SMS: a yes from a guest who hasn't answered, a no thanks from one who gets them -->
              <div class="bg-theme-surface2 rounded-2xl p-5 mt-8 text-sm">
                <p>{{ offersText(r) }}</p>
                <div class="flex justify-center mt-4">
                  @if (r.marketing === 'ask') {
                    <app-link-button font="font-medium" [text]="getString('Ja tack')" [disabled]="isSaving()"
                                     (onClick)="answerOffers(r, false)"></app-link-button>
                  } @else {
                    <app-link-button font="font-medium" [text]="getString('Nej tack, inga erbjudanden')"
                                     [disabled]="isSaving()" (onClick)="answerOffers(r, true)"></app-link-button>
                  }
                </div>
              </div>
            }

            @if (r.can_cancel && !editing()) {
              <div class="flex justify-center mt-10">
                @if (confirmCancel()) {
                  <div class="text-center">
                    <p class="text-theme-text2 mb-3">{{ getString('Vill du avboka bokningen?') }}</p>
                    <div class="flex gap-6 justify-center">
                      <app-link-button
                        color="text-red-600"
                        [text]="getString('Ja, avboka')"
                        [disabled]="isSaving()"
                        (onClick)="cancel()">
                      </app-link-button>
                      <app-link-button font="font-medium" [text]="getString('Nej')" (onClick)="confirmCancel.set(false)"></app-link-button>
                    </div>
                  </div>
                } @else {
                  <app-link-button font="font-medium" [text]="getString('Avboka')" (onClick)="confirmCancel.set(true)"></app-link-button>
                }
              </div>
            }
          }
          }
        </div>
      </div>
    </div>
  `
})
export class ReservationManageComponent implements OnInit {
  private api = inject(AuthorizedApiService);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  private venueId = '';
  private ref = '';
  private token = '';
  private timesRequest = 0;

  reservation = signal<GuestReservation | undefined>(undefined);
  isLoading = signal(true);
  loadError = signal<string | undefined>(undefined);
  actionError = signal<string | undefined>(undefined);
  notice = signal<string | undefined>(undefined);
  isSaving = signal(false);
  confirmCancel = signal(false);

  editing = signal(false);
  draft = signal<ReservationDraft>({ dateKey: '', partySize: 1 });
  private freeTimes = signal<string[]>([]);
  timesLoading = signal(false);

  sizes = computed(() => {
    const r = this.reservation();
    return r ? partySizes(r) : [];
  });
  times = computed(() => {
    const r = this.reservation();
    return r ? timesToShow(r, this.draft(), this.freeTimes()) : [];
  });
  change = computed(() => {
    const r = this.reservation();
    return r ? reservationChange(r, this.draft()) : undefined;
  });

  getString(s: string) {
    return this.translate.getString(s);
  }

  ngOnInit() {
    this.venueId = this.route.snapshot.paramMap.get('venue_id') ?? '';
    this.ref = this.route.snapshot.paramMap.get('ref') ?? '';
    this.token = this.route.snapshot.queryParamMap.get('t') ?? '';
    if (!this.ref || !this.token) {
      this.isLoading.set(false);
      this.loadError.set(this.getString('Länken är ofullständig. Öppna länken i ditt SMS eller mejl igen.'));
      return;
    }
    this.api.reservationStatus(this.venueId, this.ref, this.token).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.success && response.reservation) {
          this.reservation.set(response.reservation);
        } else {
          this.loadError.set(this.errorText(response.error));
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set(this.getString('Det gick inte att hämta bokningen. Försök igen om en stund.'));
      }
    });
  }

  childrenSuffix(r: GuestReservation): string {
    return r.children_count > 0 ? ` ${this.getString('(varav %s barn)').replace('%s', String(r.children_count))}` : '';
  }

  formatDate(dateKey?: string): string {
    if (!dateKey) return '';
    const date = new Date(dateKey);
    return date.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  }

  startEditing(r: GuestReservation) {
    this.notice.set(undefined);
    this.actionError.set(undefined);
    this.confirmCancel.set(false);
    this.draft.set({ dateKey: r.date_key, partySize: r.party_size, time: r.time });
    this.editing.set(true);
    this.loadTimes();
  }

  stopEditing() {
    this.editing.set(false);
    this.actionError.set(undefined);
  }

  setParty(event: Event) {
    const partySize = Number((event.target as HTMLSelectElement).value);
    this.draft.update(d => ({ ...d, partySize, time: undefined }));
    this.loadTimes();
  }

  setDate(event: Event) {
    const dateKey = (event.target as HTMLSelectElement).value;
    this.draft.update(d => ({ ...d, dateKey, time: undefined }));
    this.loadTimes();
  }

  setTime(time: string) {
    this.draft.update(d => ({ ...d, time }));
  }

  private loadTimes() {
    const { dateKey, partySize } = this.draft();
    const request = ++this.timesRequest;
    this.timesLoading.set(true);
    this.freeTimes.set([]);
    this.api.reservationTimes(this.venueId, this.ref, this.token, dateKey, partySize).subscribe({
      next: (response) => {
        if (request !== this.timesRequest) return;
        this.timesLoading.set(false);
        this.freeTimes.set(response.times ?? []);
        this.actionError.set(response.success ? undefined : this.errorText(response.error));
      },
      error: () => {
        if (request !== this.timesRequest) return;
        this.timesLoading.set(false);
        this.actionError.set(this.getString('Det gick inte att hämta lediga tider. Försök igen om en stund.'));
      }
    });
  }

  save() {
    const change = this.change();
    if (!change) return;
    this.isSaving.set(true);
    this.actionError.set(undefined);
    this.api.reservationChange(this.venueId, this.ref, this.token, change).subscribe({
      next: (response) => {
        this.isSaving.set(false);
        if (response.success && response.reservation) {
          this.reservation.set(response.reservation);
          this.editing.set(false);
          this.notice.set(this.getString('Bokningen är ändrad.'));
          return;
        }
        this.actionError.set(this.errorText(response.error));
        if (response.error === 'time_not_available') {
          this.freeTimes.set(response.free_times ?? []);
          this.draft.update(d => ({ ...d, time: undefined }));
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.actionError.set(this.getString('Det gick inte att spara ändringen. Försök igen.'));
      }
    });
  }

  confirm() {
    this.isSaving.set(true);
    this.actionError.set(undefined);
    this.api.reservationConfirm(this.venueId, this.ref, this.token).subscribe({
      next: (response: GuestReservationResponse) => {
        this.isSaving.set(false);
        if (response.success && response.reservation) {
          this.reservation.set(response.reservation);
          this.notice.set(this.getString('Tack! Din bokning är bekräftad.'));
        } else {
          this.actionError.set(this.errorText(response.error));
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.actionError.set(this.getString('Det gick inte att bekräfta. Försök igen.'));
      }
    });
  }

  cancel() {
    this.isSaving.set(true);
    this.actionError.set(undefined);
    this.api.reservationCancel(this.venueId, this.ref, this.token).subscribe({
      next: (response: GuestReservationResponse) => {
        this.isSaving.set(false);
        this.confirmCancel.set(false);
        if (response.success && response.reservation) {
          this.reservation.set(response.reservation);
          this.notice.set(undefined);
        } else {
          this.actionError.set(this.errorText(response.error));
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.actionError.set(this.getString('Det gick inte att avboka. Försök igen.'));
      }
    });
  }

  offersText(r: GuestReservation): string {
    const text = r.marketing === 'ask' ? OFFERS_ASK : OFFERS_RECEIVES;
    return this.getString(text).replace('%s', r.marketing_name ?? r.venue_name);
  }

  // Yes to the offers text shown, or no thanks; samwise logs it on the guest's profile
  answerOffers(r: GuestReservation, objected: boolean) {
    this.isSaving.set(true);
    this.actionError.set(undefined);
    const name = r.marketing_name ?? r.venue_name;
    this.api.reservationMarketing(this.venueId, this.ref, this.token, this.offersText(r), objected).subscribe({
      next: (response: GuestReservationResponse) => {
        this.isSaving.set(false);
        if (response.success && response.reservation) {
          this.reservation.set(response.reservation);
          const done = objected ? 'Du får inga erbjudanden från %s.' : 'Tack! Du får erbjudanden från %s.';
          this.notice.set(this.getString(done).replace('%s', name));
        } else {
          this.actionError.set(this.errorText(response.error));
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.actionError.set(this.getString('Det gick inte att spara. Försök igen.'));
      }
    });
  }

  private errorText(code?: string): string {
    return this.getString(RESERVATION_ERRORS[code ?? ''] ?? 'Ett fel uppstod. Försök igen.');
  }
}
